import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  fetchNotifications,
  deleteNotification,
  sendAppToUser,
  sendAppToUsers,
  sendWhatsAppToUser,
  sendWhatsAppToUsers,
  sendWhatsAppToAdmin,
  sendWhatsAppToAdmins,
} from "../notificationsApi";
import { fetchUsers } from "../usersApi";     // ← adjust path
import { fetchAdmins } from "../adminsApi";   // ← adjust path
import config from "../config";
import {
  Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Typography, IconButton, Box, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, Stack, FormGroup, FormControlLabel, Checkbox, Alert,
  CircularProgress, Divider, Card, CardContent, CardMedia, Badge, Tooltip,
  Collapse, useTheme, alpha, TextField, InputAdornment, ToggleButton,
  ToggleButtonGroup, Autocomplete, Avatar,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import DeleteIcon from "@mui/icons-material/Delete";
import AddBoxIcon from "@mui/icons-material/AddBox";
import PeopleIcon from "@mui/icons-material/People";
import PersonIcon from "@mui/icons-material/Person";
import GroupIcon from "@mui/icons-material/Group";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import SendIcon from "@mui/icons-material/Send";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import ImageIcon from "@mui/icons-material/Image";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import InfoIcon from "@mui/icons-material/Info";
import PublicIcon from "@mui/icons-material/Public";
import Swal from "sweetalert2";

// ─── Helpers ────────────────────────────────────────────
const getDisplayName = (p) =>
  p?.Name || p?.FullName || p?.Username || p?.Number || p?.Phone || `#${p?.Id}`;
const getDisplayPhone = (p) =>
  p?.Number || p?.Phone || p?.PhoneNumber || "";

// ═══════════════════════════════════════════════════════════════
// Reusable Channel Block (mode selector + recipient picker)
// ═══════════════════════════════════════════════════════════════
function ChannelBlock({
  enabled, setEnabled,
  mode, setMode,
  selected, setSelected,
  color, title, desc, icon,
  options, optionLabel = "user", loadingOptions,
}) {
  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 2,
        transition: "all 0.3s ease",
        bgcolor: enabled ? alpha(color, 0.06) : "background.paper",
        borderColor: enabled ? color : "divider",
        borderWidth: enabled ? 2 : 1,
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      {/* Toggle row */}
      <FormControlLabel
        sx={{ p: 2.5, m: 0, width: "100%" }}
        control={
          <Checkbox
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            sx={{ color: color, "&.Mui-checked": { color: color } }}
            size="large"
          />
        }
        label={
          <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "14px",
                background: `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.7)} 100%)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: `0 4px 12px ${alpha(color, 0.3)}`,
              }}
            >
              {icon}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body1" fontWeight="700" sx={{ mb: 0.5 }}>
                {title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {desc}
              </Typography>
            </Box>
          </Stack>
        }
      />

      {/* Mode selector + picker */}
      <Collapse in={enabled}>
        <Divider />
        <Box sx={{ p: 2.5, bgcolor: alpha(color, 0.02) }}>
          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: "block", mb: 1.5, textTransform: "uppercase", letterSpacing: 0.5 }}>
            {t("recipients") || "Recipients"}
          </Typography>

          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={(_, v) => {
              if (v) {
                setMode(v);
                if (v === "all") setSelected([]);
              }
            }}
            size="small"
            fullWidth
            sx={{
              mb: mode !== "all" ? 2 : 0,
              "& .MuiToggleButton-root": {
                textTransform: "none",
                fontWeight: 600,
                borderColor: alpha(color, 0.3),
                "&.Mui-selected": {
                  bgcolor: color,
                  color: "white",
                  "&:hover": { bgcolor: color },
                },
              },
            }}
          >
            <ToggleButton value="all">
              <PublicIcon sx={{ fontSize: 16, mr: 0.5 }} />
              {t("all") || "All"}
            </ToggleButton>
            <ToggleButton value="selected">
              <GroupIcon sx={{ fontSize: 16, mr: 0.5 }} />
              {t("selected") || "Selected"}
            </ToggleButton>
            <ToggleButton value="single">
              <PersonIcon sx={{ fontSize: 16, mr: 0.5 }} />
              {t("single") || "Single"}
            </ToggleButton>
          </ToggleButtonGroup>

          {/* Picker */}
          {mode === "selected" && (
            <Autocomplete
              multiple
              size="small"
              loading={loadingOptions}
              options={options}
              value={options.filter((o) => selected.includes(o.Id))}
              onChange={(_, newVal) => setSelected(newVal.map((v) => v.Id))}
              getOptionLabel={(o) => `${getDisplayName(o)}${getDisplayPhone(o) ? ` — ${getDisplayPhone(o)}` : ""}`}
              isOptionEqualToValue={(o, v) => o.Id === v.Id}
              filterSelectedOptions
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.Id}
                    avatar={<Avatar sx={{ bgcolor: color }}>{getDisplayName(option).charAt(0).toUpperCase()}</Avatar>}
                    label={getDisplayName(option)}
                    size="small"
                    sx={{ bgcolor: alpha(color, 0.1), color: color, fontWeight: 600 }}
                  />
                ))
              }
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.Id} sx={{ gap: 1 }}>
                  <Avatar sx={{ bgcolor: alpha(color, 0.2), color: color, width: 32, height: 32, fontSize: 14, fontWeight: 700 }}>
                    {getDisplayName(option).charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight={600}>{getDisplayName(option)}</Typography>
                    {getDisplayPhone(option) && (
                      <Typography variant="caption" color="text.secondary">{getDisplayPhone(option)}</Typography>
                    )}
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={`${t("search") || "Search"} ${optionLabel}s...`}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingOptions && <CircularProgress size={18} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          )}

          {mode === "single" && (
            <Autocomplete
              size="small"
              loading={loadingOptions}
              options={options}
              value={options.find((o) => o.Id === selected[0]) || null}
              onChange={(_, newVal) => setSelected(newVal ? [newVal.Id] : [])}
              getOptionLabel={(o) => `${getDisplayName(o)}${getDisplayPhone(o) ? ` — ${getDisplayPhone(o)}` : ""}`}
              isOptionEqualToValue={(o, v) => o.Id === v.Id}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.Id} sx={{ gap: 1 }}>
                  <Avatar sx={{ bgcolor: alpha(color, 0.2), color: color, width: 32, height: 32, fontSize: 14, fontWeight: 700 }}>
                    {getDisplayName(option).charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight={600}>{getDisplayName(option)}</Typography>
                    {getDisplayPhone(option) && (
                      <Typography variant="caption" color="text.secondary">{getDisplayPhone(option)}</Typography>
                    )}
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={`${t("selectA") || "Select a"} ${optionLabel}...`}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingOptions && <CircularProgress size={18} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          )}

          {/* Summary */}
          {mode !== "all" && selected.length > 0 && (
            <Chip
              size="small"
              label={`${selected.length} ${mode === "single" ? (t("recipient") || "recipient") : (t("recipients") || "recipients")} ${t("selected") || "selected"}`}
              sx={{ mt: 1.5, bgcolor: color, color: "white", fontWeight: 600 }}
            />
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════
export default function Notifications() {
  const [notifications, setNotifications] = useState([]);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedRows, setExpandedRows] = useState(new Set());

  // ─── 3 channels × (enabled + mode + selectedIds) ───
  const [appEnabled, setAppEnabled] = useState(true);
  const [appMode, setAppMode] = useState("all"); // all | selected | single
  const [appSelected, setAppSelected] = useState([]);

  const [waUEnabled, setWaUEnabled] = useState(false);
  const [waUMode, setWaUMode] = useState("all");
  const [waUSelected, setWaUSelected] = useState([]);

  const [waAEnabled, setWaAEnabled] = useState(false);
  const [waAMode, setWaAMode] = useState("all");
  const [waASelected, setWaASelected] = useState([]);

  // ─── Users / Admins lists ───
  const [usersList, setUsersList] = useState([]);
  const [adminsList, setAdminsList] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingAdmins, setLoadingAdmins] = useState(false);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();

  // ─── Load ────────────────────────────────────────────
  const loadNotifications = async () => {
    try {
      const notis = await fetchNotifications();
      const filtered = Array.isArray(notis)
        ? notis.filter((noti) => !/\-\d{6}$/.test(noti.Type ?? ""))
        : [];
      setNotifications(filtered);
    } catch (error) {
      console.error("Error loading notifications:", error);
      Swal.fire({ icon: "error", title: t("Error"), text: "Failed to load notifications" });
    }
  };

  useEffect(() => { loadNotifications(); }, []);

  // Lazy load users/admins when dialog opens
  const ensureUsersLoaded = async () => {
    if (usersList.length > 0 || loadingUsers) return;
    setLoadingUsers(true);
    try {
      const data = await fetchUsers();
      setUsersList(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Error loading users:", e);
    } finally {
      setLoadingUsers(false);
    }
  };

  const ensureAdminsLoaded = async () => {
    if (adminsList.length > 0 || loadingAdmins) return;
    setLoadingAdmins(true);
    try {
      const data = await fetchAdmins();
      setAdminsList(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Error loading admins:", e);
    } finally {
      setLoadingAdmins(false);
    }
  };

  // ─── Delete ──────────────────────────────────────────
  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: t("areYouSure") || "Are you sure?",
      text: t("deleteNotificationWarning") || "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: theme.palette.grey[400],
      confirmButtonText: t("yesDelete") || "Yes, delete it!",
      cancelButtonText: t("cancel") || "Cancel",
      reverseButtons: true,
    });
    if (result.isConfirmed) {
      try {
        await deleteNotification(id);
        await loadNotifications();
        Swal.fire({ icon: "success", title: t("deleted") || "Deleted!", text: t("notificationDeleted") || "Notification has been deleted.", timer: 2000 });
      } catch {
        Swal.fire({ icon: "error", title: t("Error"), text: "Failed to delete notification" });
      }
    }
  };

  const handleOpenSendDialog = (notification) => {
    setSelectedNotification(notification);
    setAppEnabled(true); setAppMode("all"); setAppSelected([]);
    setWaUEnabled(false); setWaUMode("all"); setWaUSelected([]);
    setWaAEnabled(false); setWaAMode("all"); setWaASelected([]);
    setSendDialogOpen(true);
    // preload in background
    ensureUsersLoaded();
    ensureAdminsLoaded();
  };

  const handleCloseSendDialog = () => {
    setSendDialogOpen(false);
    setSelectedNotification(null);
  };

  const handleOpenPreview = (notification) => {
    setSelectedNotification(notification);
    setPreviewDialogOpen(true);
  };
  const handleClosePreview = () => {
    setPreviewDialogOpen(false);
    setSelectedNotification(null);
  };

  const toggleRowExpansion = (id) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      return newSet;
    });
  };

  // ═══════════════════════════════════════════════════════
  // SEND
  // ═══════════════════════════════════════════════════════
  const handleSendNotification = async () => {
    if (!appEnabled && !waUEnabled && !waAEnabled) {
      Swal.fire({
        icon: "warning",
        title: t("noChannelSelected") || "No Channel Selected",
        text: t("selectAtLeastOneChannel") || "Please select at least one delivery channel",
      });
      return;
    }

    // Validate recipient selections
    if (appEnabled && appMode !== "all" && appSelected.length === 0) {
      Swal.fire({ icon: "warning", title: t("Error"), text: "Please pick recipients for App Push" });
      return;
    }
    if (waUEnabled && waUMode !== "all" && waUSelected.length === 0) {
      Swal.fire({ icon: "warning", title: t("Error"), text: "Please pick recipients for WhatsApp → Users" });
      return;
    }
    if (waAEnabled && waAMode !== "all" && waASelected.length === 0) {
      Swal.fire({ icon: "warning", title: t("Error"), text: "Please pick recipients for WhatsApp → Admins" });
      return;
    }

    setSending(true);
    const results = {};

    try {
      // 1️⃣ App push
      if (appEnabled) {
        try {
          if (appMode === "single") {
            results.app = await sendAppToUser({ userId: appSelected[0], notificationId: selectedNotification.Id });
          } else if (appMode === "selected") {
            results.app = await sendAppToUsers({ userIds: appSelected, notificationId: selectedNotification.Id });
          } else {
            results.app = await sendAppToUsers({ notificationId: selectedNotification.Id });
          }
        } catch (error) {
          results.app = { error: error.response?.data?.message || error.message };
        }
      }

      // 2️⃣ WhatsApp → Users
      if (waUEnabled) {
        try {
          if (waUMode === "single") {
            results.waUsers = await sendWhatsAppToUser({ userId: waUSelected[0], notificationId: selectedNotification.Id });
          } else if (waUMode === "selected") {
            results.waUsers = await sendWhatsAppToUsers({ userIds: waUSelected, notificationId: selectedNotification.Id });
          } else {
            results.waUsers = await sendWhatsAppToUsers({ notificationId: selectedNotification.Id });
          }
        } catch (error) {
          results.waUsers = { error: error.response?.data?.message || error.message };
        }
      }

      // 3️⃣ WhatsApp → Admins
      if (waAEnabled) {
        try {
          const payload = {
            Title: selectedNotification.Title,
            Subtitle: selectedNotification.Subtitle,
            Message: selectedNotification.Message,
            Image: selectedNotification.Image,
          };
          if (waAMode === "single") {
            results.waAdmins = await sendWhatsAppToAdmin({ adminId: waASelected[0], ...payload });
          } else if (waAMode === "selected") {
            results.waAdmins = await sendWhatsAppToAdmins({ adminIds: waASelected, ...payload });
          } else {
            results.waAdmins = await sendWhatsAppToAdmins(payload);
          }
        } catch (error) {
          results.waAdmins = { error: error.response?.data?.message || error.message };
        }
      }

      // Build summary HTML
      let successHtml = '<div style="text-align: left; font-family: system-ui;">';

      if (results.app) {
        if (results.app.error) {
          successHtml += `<div style="margin-bottom:16px;padding:14px;background:#ffebee;border-left:4px solid #f44336;border-radius:8px;">
            <h4 style="margin:0 0 6px 0;color:#c62828;">📱 App Push — Failed</h4>
            <p style="margin:0;font-size:13px;color:#c62828;">${results.app.error}</p></div>`;
        } else {
          const d = results.app.details || {};
          const modeLabel = appMode === "single" ? "1 user" : appMode === "selected" ? `${appSelected.length} selected` : "all users";
          successHtml += `<div style="margin-bottom:16px;padding:16px;background:linear-gradient(135deg,#1976d2 0%,#1565c0 100%);border-radius:12px;color:white;">
            <h3 style="margin:0 0 10px 0;"><span style="font-size:22px;">📱</span> App Push — ${modeLabel}</h3>
            <div style="background:rgba(255,255,255,0.1);padding:12px;border-radius:8px;">
              <p style="margin:4px 0;font-size:13px;"><strong>Total:</strong> ${d.total || 0}</p>
              <p style="margin:4px 0;font-size:13px;color:#a5d6a7;"><strong>Saved:</strong> ${d.saved || 0}</p>
              <p style="margin:4px 0;font-size:13px;color:#a5d6a7;"><strong>Pushed:</strong> ${d.pushed || 0}</p>
            </div></div>`;
        }
      }

      if (results.waUsers) {
        if (results.waUsers.error) {
          successHtml += `<div style="margin-bottom:16px;padding:14px;background:#ffebee;border-left:4px solid #f44336;border-radius:8px;">
            <h4 style="margin:0 0 6px 0;color:#c62828;">💬 WhatsApp Users — Failed</h4>
            <p style="margin:0;font-size:13px;color:#c62828;">${results.waUsers.error}</p></div>`;
        } else {
          const d = results.waUsers.details || {};
          const modeLabel = waUMode === "single" ? "1 user" : waUMode === "selected" ? `${waUSelected.length} selected` : "all users";
          successHtml += `<div style="margin-bottom:16px;padding:16px;background:linear-gradient(135deg,#25D366 0%,#128C7E 100%);border-radius:12px;color:white;">
            <h3 style="margin:0 0 10px 0;"><span style="font-size:22px;">💬</span> WhatsApp Users — ${modeLabel}</h3>
            <div style="background:rgba(255,255,255,0.1);padding:12px;border-radius:8px;">
              <p style="margin:4px 0;font-size:13px;"><strong>Total:</strong> ${d.total || 0}</p>
              <p style="margin:4px 0;font-size:13px;color:#c8e6c9;"><strong>Sent:</strong> ${d.sent || 0}</p>
              <p style="margin:4px 0;font-size:13px;color:#ef9a9a;"><strong>Failed:</strong> ${d.failed || 0}</p>
            </div></div>`;
        }
      }

      if (results.waAdmins) {
        if (results.waAdmins.error) {
          successHtml += `<div style="margin-bottom:16px;padding:14px;background:#ffebee;border-left:4px solid #f44336;border-radius:8px;">
            <h4 style="margin:0 0 6px 0;color:#c62828;">👨‍💼 WhatsApp Admins — Failed</h4>
            <p style="margin:0;font-size:13px;color:#c62828;">${results.waAdmins.error}</p></div>`;
        } else {
          const d = results.waAdmins.details || {};
          const modeLabel = waAMode === "single" ? "1 admin" : waAMode === "selected" ? `${waASelected.length} selected` : "all admins";
          successHtml += `<div style="padding:16px;background:linear-gradient(135deg,#9c27b0 0%,#7b1fa2 100%);border-radius:12px;color:white;">
            <h3 style="margin:0 0 10px 0;"><span style="font-size:22px;">👨‍💼</span> WhatsApp Admins — ${modeLabel}</h3>
            <div style="background:rgba(255,255,255,0.1);padding:12px;border-radius:8px;">
              <p style="margin:4px 0;font-size:13px;"><strong>Total:</strong> ${d.total || 0}</p>
              <p style="margin:4px 0;font-size:13px;color:#c8e6c9;"><strong>Sent:</strong> ${d.sent || 0}</p>
              <p style="margin:4px 0;font-size:13px;color:#ef9a9a;"><strong>Failed:</strong> ${d.failed || 0}</p>
            </div></div>`;
        }
      }

      successHtml += "</div>";

      const hasErrors = results.app?.error || results.waUsers?.error || results.waAdmins?.error;

      await Swal.fire({
        icon: hasErrors ? "warning" : "success",
        title: hasErrors ? (t("partialSuccess") || "Partial Success") : (t("Success") || "Success!"),
        html: successHtml,
        width: 650,
        confirmButtonColor: theme.palette.success.main,
        customClass: { popup: "swal-modern-popup" },
      });

      handleCloseSendDialog();
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || "Failed to send notification";
      Swal.fire({ icon: "error", title: t("Error"), text: errorMessage });
    } finally {
      setSending(false);
    }
  };

  const filteredNotifications = notifications.filter(
    (noti) =>
      noti.Title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      noti.Subtitle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      noti.Type?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const anyChannelSelected = appEnabled || waUEnabled || waAEnabled;

  // ═══════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════
  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Card
        elevation={0}
        sx={{
          mb: 3,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: "white",
          borderRadius: 3,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems="center" spacing={2}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box sx={{ bgcolor: alpha("#fff", 0.2), p: 1.5, borderRadius: 2, display: "flex" }}>
                <NotificationsActiveIcon sx={{ fontSize: 32 }} />
              </Box>
              <Box>
                <Typography variant="h4" fontWeight="800">{t("Notifications")}</Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {t("manageAndSendNotifications") || "Manage and send notifications"}
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              size="large"
              startIcon={<AddBoxIcon />}
              onClick={() => navigate("/admin/notifications/add")}
              sx={{
                bgcolor: "white",
                color: theme.palette.primary.main,
                fontWeight: 700,
                px: 3,
                py: 1.5,
                borderRadius: 2,
                boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
                "&:hover": {
                  bgcolor: alpha("#fff", 0.95),
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 20px rgba(0,0,0,0.2)",
                },
                transition: "all 0.3s",
              }}
            >
              {t("AddNotification")}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Search */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
          <TextField
            fullWidth
            size="small"
            placeholder={t("searchNotifications") || "Search notifications..."}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />
          <Chip
            icon={<FilterListIcon />}
            label={`${filteredNotifications.length} ${t("results") || "results"}`}
            color="primary"
            variant="outlined"
            sx={{ minWidth: 120 }}
          />
        </Stack>
      </Paper>

      {/* Table */}
      <Card elevation={0} sx={{ borderRadius: 3, overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08) }}>
                <TableCell sx={{ fontWeight: 700, fontSize: "0.875rem" }}>ID</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: "0.875rem" }}>{t("NotificationTitle")}</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: "0.875rem" }}>{t("NotificationSubtitle")}</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: "0.875rem" }}>{t("NotificationType")}</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: "0.875rem" }}>{t("NotificationCreatedTime")}</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: "0.875rem" }}>{t("NotificationImage") || "Image"}</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: "0.875rem", textAlign: "center" }}>
                  {t("Actions") || "Actions"}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredNotifications.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <NotificationsActiveIcon sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      {searchTerm ? t("noResultsFound") || "No results found" : t("noNotifications") || "No notifications found"}
                    </Typography>
                    <Typography variant="body2" color="text.disabled">
                      {searchTerm ? t("tryDifferentSearch") || "Try a different search term" : t("createFirstNotification") || "Create your first notification to get started"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredNotifications.map((noti) => (
                  <React.Fragment key={noti.Id}>
                    <TableRow hover sx={{ "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.02) }, transition: "background-color 0.2s" }}>
                      <TableCell>
                        <Chip label={`#${noti.Id}`} size="small" sx={{ fontWeight: 600 }} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>{noti.Title}</Typography>
                        {noti.Message && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                            {noti.Message.substring(0, 50)}{noti.Message.length > 50 ? "..." : ""}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{noti.Subtitle || "-"}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={noti.Type} size="small" color="primary" variant="outlined" sx={{ fontWeight: 600 }} />
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <CalendarTodayIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                            <Typography variant="body2" fontWeight={500}>
                              {new Date(noti.CreatedTime).toLocaleDateString()}
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(noti.CreatedTime).toLocaleTimeString()}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {noti.Image ? (
                          <Badge
                            overlap="circular"
                            anchorOrigin={{ vertical: "top", horizontal: "right" }}
                            badgeContent={<ImageIcon sx={{ fontSize: 16, color: "success.main" }} />}
                          >
                            <Box
                              component="img"
                              src={noti.Image.startsWith("http") ? noti.Image : `${config.API_BASE_URL}/${noti.Image.replace(/^\//, "")}`}
                              alt="Notification"
                              sx={{
                                width: 70, height: 70, objectFit: "cover", borderRadius: 2,
                                border: `2px solid ${theme.palette.divider}`,
                                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                              }}
                            />
                          </Badge>
                        ) : (
                          <Box
                            sx={{
                              width: 70, height: 70, borderRadius: 2,
                              border: `2px dashed ${theme.palette.divider}`,
                              display: "flex", alignItems: "center", justifyContent: "center",
                              bgcolor: alpha(theme.palette.grey[500], 0.05),
                            }}
                          >
                            <ImageIcon sx={{ color: "text.disabled", fontSize: 28 }} />
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        <Stack direction="column" spacing={1} alignItems="center">
                          <Stack direction="row" spacing={0.5}>
                            <Tooltip title={t("preview") || "Preview"} arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenPreview(noti)}
                                sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), "&:hover": { bgcolor: alpha(theme.palette.info.main, 0.2) } }}
                              >
                                <VisibilityIcon fontSize="small" color="info" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t("edit") || "Edit"} arrow>
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/admin/notifications/edit/${noti.Id}`)}
                                sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.2) } }}
                              >
                                <EditIcon fontSize="small" color="primary" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t("delete") || "Delete"} arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(noti.Id)}
                                sx={{ bgcolor: alpha(theme.palette.error.main, 0.1), "&:hover": { bgcolor: alpha(theme.palette.error.main, 0.2) } }}
                              >
                                <DeleteIcon fontSize="small" color="error" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                          <Button
                            fullWidth
                            size="small"
                            variant="contained"
                            color="success"
                            startIcon={<SendIcon />}
                            onClick={() => handleOpenSendDialog(noti)}
                            sx={{
                              borderRadius: 2,
                              textTransform: "none",
                              fontWeight: 600,
                              py: 0.75,
                              boxShadow: "0 2px 8px rgba(76, 175, 80, 0.3)",
                              "&:hover": { boxShadow: "0 4px 12px rgba(76, 175, 80, 0.4)" },
                            }}
                          >
                            {t("Send") || "Send"}
                          </Button>
                          {noti.Message && noti.Message.length > 50 && (
                            <Button
                              size="small"
                              endIcon={expandedRows.has(noti.Id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              onClick={() => toggleRowExpansion(noti.Id)}
                              sx={{ textTransform: "none", fontSize: "0.75rem" }}
                            >
                              {expandedRows.has(noti.Id) ? t("showLess") || "Show less" : t("showMore") || "Show more"}
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={7} sx={{ p: 0, border: "none" }}>
                        <Collapse in={expandedRows.has(noti.Id)} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 3, bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ mb: 2 }}>
                              {t("fullMessage") || "Full Message"}:
                            </Typography>
                            <Paper sx={{ p: 2, bgcolor: "background.paper", borderRadius: 2 }}>
                              <Typography
                                variant="body2"
                                sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.8 }}
                              >
                                {noti.Message}
                              </Typography>
                            </Paper>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* ═══════════════════════════════════════════════════ */}
      {/* PREVIEW DIALOG */}
      {/* ═══════════════════════════════════════════════════ */}
      <Dialog
        open={previewDialogOpen}
        onClose={handleClosePreview}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
        sx={{ "& .MuiDialog-container": { zIndex: 9999 }, "& .MuiBackdrop-root": { zIndex: 9998 } }}
      >
        <DialogTitle sx={{ pb: 1, background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`, color: "white" }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <VisibilityIcon />
            <Typography variant="h6" fontWeight="700">{t("notificationPreview") || "Notification Preview"}</Typography>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, px: 0 }}>
          {selectedNotification && (
            <Box>
              <Box sx={{ px: 3, mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <WhatsAppIcon sx={{ fontSize: 18, color: "#25D366" }} />
                  {t("whatsappPreview") || "WhatsApp Preview"}
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5, bgcolor: "#DCF8C6", borderRadius: "0 12px 12px 12px",
                    position: "relative", border: "1px solid #B3E5A1",
                    "&::before": {
                      content: '""', position: "absolute", top: 0, left: -8,
                      width: 0, height: 0, borderStyle: "solid",
                      borderWidth: "0 8px 8px 0",
                      borderColor: `transparent #DCF8C6 transparent transparent`,
                    },
                  }}
                >
                  {selectedNotification.Image && (
                    <Box
                      component="img"
                      src={selectedNotification.Image.startsWith("http") ? selectedNotification.Image : `${config.API_BASE_URL}/${selectedNotification.Image.replace(/^\//, "")}`}
                      alt="Notification"
                      sx={{ width: "100%", maxHeight: 200, objectFit: "cover", borderRadius: 2, mb: 1.5 }}
                    />
                  )}
                  <Typography variant="subtitle1" fontWeight="700" sx={{ mb: 1, color: "#075E54" }}>
                    {selectedNotification.Title}
                  </Typography>
                  {selectedNotification.Subtitle && (
                    <Typography variant="body2" sx={{ mb: 1.5, color: "#128C7E", fontWeight: 500 }}>
                      {selectedNotification.Subtitle}
                    </Typography>
                  )}
                  <Typography
                    variant="body2"
                    sx={{ color: "#303030", whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.6 }}
                  >
                    {selectedNotification.Message}
                  </Typography>
                  <Typography variant="caption" sx={{ display: "block", textAlign: "right", mt: 1, color: "#667781" }}>
                    {new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </Typography>
                </Paper>
              </Box>

              <Divider />

              <Box sx={{ px: 3, pt: 3 }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ mb: 2 }}>
                  {t("details") || "Details"}
                </Typography>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
                      {t("type") || "Type"}
                    </Typography>
                    <Chip label={selectedNotification.Type} size="small" color="primary" sx={{ mt: 0.5 }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
                      {t("created") || "Created"}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {new Date(selectedNotification.CreatedTime).toLocaleString()}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Box>
          )}
        </DialogContent>

        <Divider />

        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={handleClosePreview} variant="outlined" size="large">
            {t("close") || "Close"}
          </Button>
          <Button
            onClick={() => {
              handleClosePreview();
              handleOpenSendDialog(selectedNotification);
            }}
            variant="contained"
            color="success"
            size="large"
            startIcon={<SendIcon />}
          >
            {t("sendThisNotification") || "Send This Notification"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══════════════════════════════════════════════════ */}
      {/* SEND DIALOG — 3 INDEPENDENT CHANNELS WITH MODES    */}
      {/* ═══════════════════════════════════════════════════ */}
      <Dialog
        open={sendDialogOpen}
        onClose={handleCloseSendDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
        sx={{ "& .MuiDialog-container": { zIndex: 9999 }, "& .MuiBackdrop-root": { zIndex: 9998 } }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
            color: "white",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <SendIcon />
            <Typography variant="h6" fontWeight="700">
              {t("sendNotification") || "Send Notification"}
            </Typography>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          {/* Notification Preview Card */}
          {selectedNotification && (
            <Card
              elevation={0}
              sx={{
                mb: 3,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
                border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                borderRadius: 3,
              }}
            >
              <CardContent sx={{ p: 3 }}>
                {selectedNotification.Image && (
                  <CardMedia
                    component="img"
                    height="150"
                    image={
                      selectedNotification.Image.startsWith("http")
                        ? selectedNotification.Image
                        : `${config.API_BASE_URL}/${selectedNotification.Image.replace(/^\//, "")}`
                    }
                    alt="Notification"
                    sx={{ borderRadius: 2, mb: 2, objectFit: "cover" }}
                  />
                )}
                <Typography variant="h6" fontWeight="700" gutterBottom color="primary.main">
                  {selectedNotification.Title}
                </Typography>
                {selectedNotification.Subtitle && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 500 }}>
                    {selectedNotification.Subtitle}
                  </Typography>
                )}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    lineHeight: 1.7,
                    maxHeight: 150,
                    overflowY: "auto",
                    pr: 1,
                  }}
                >
                  {selectedNotification.Message}
                </Typography>
              </CardContent>
            </Card>
          )}

          {/* ─── 📱 APP PUSH SECTION ─── */}
          <Box sx={{ mb: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <PhoneAndroidIcon sx={{ color: "#1976d2" }} />
              <Typography variant="h6" fontWeight="700">
                📱 {t("appNotifications") || "App Notifications"}
              </Typography>
            </Stack>

            <ChannelBlock
              enabled={appEnabled}
              setEnabled={setAppEnabled}
              mode={appMode}
              setMode={setAppMode}
              selected={appSelected}
              setSelected={setAppSelected}
              color="#1976d2"
              title={t("appPush") || "App Push"}
              desc={t("savesAndSendsExpoPush") || "Saves to DB + sends Expo push notification"}
              icon={<PhoneAndroidIcon sx={{ color: "white", fontSize: 32 }} />}
              options={usersList}
              optionLabel="user"
              loadingOptions={loadingUsers}
            />
          </Box>

          {/* ─── 💬 WHATSAPP CHANNELS ─── */}
          <Box sx={{ mb: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <WhatsAppIcon sx={{ color: "#25D366" }} />
              <Typography variant="h6" fontWeight="700">
                💬 {t("whatsappChannels") || "WhatsApp Channels"}
              </Typography>
            </Stack>

            {/* WhatsApp → Users */}
            <ChannelBlock
              enabled={waUEnabled}
              setEnabled={setWaUEnabled}
              mode={waUMode}
              setMode={setWaUMode}
              selected={waUSelected}
              setSelected={setWaUSelected}
              color="#25D366"
              title={t("whatsappUsers") || "WhatsApp → Users"}
              desc={t("sendWaToCustomers") || "Send WhatsApp to customers (no DB save)"}
              icon={<PeopleIcon sx={{ color: "white", fontSize: 32 }} />}
              options={usersList}
              optionLabel="user"
              loadingOptions={loadingUsers}
            />

            {/* WhatsApp → Admins */}
            <ChannelBlock
              enabled={waAEnabled}
              setEnabled={setWaAEnabled}
              mode={waAMode}
              setMode={setWaAMode}
              selected={waASelected}
              setSelected={setWaASelected}
              color="#9c27b0"
              title={t("whatsappAdmins") || "WhatsApp → Admins"}
              desc={t("sendWaToAdmins") || "Send WhatsApp to admins (no DB save)"}
              icon={<AdminPanelSettingsIcon sx={{ color: "white", fontSize: 32 }} />}
              options={adminsList}
              optionLabel="admin"
              loadingOptions={loadingAdmins}
            />
          </Box>

          {/* Info Box */}
          {anyChannelSelected && (
            <Alert
              severity="info"
              icon={<InfoIcon />}
              sx={{
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                "& .MuiAlert-icon": { fontSize: 28 },
              }}
            >
              <Typography variant="body2" fontWeight="600" sx={{ mb: 0.5 }}>
                {t("separateChannelsInfo") || "Each channel runs independently."}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                📱 {t("appPushSavesToDb") || "App Push saves to DB + sends Expo push."} &nbsp;•&nbsp;
                💬 {t("whatsappNoDbSave") || "WhatsApp does NOT save to DB."}
              </Typography>
              <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 0.5 }}>
                {appEnabled && (
                  <Typography variant="caption" sx={{ color: "#1976d2", fontWeight: 600 }}>
                    ✓ App push →{" "}
                    {appMode === "all"
                      ? t("allUsers") || "all users"
                      : appMode === "single"
                      ? `1 ${t("user") || "user"}`
                      : `${appSelected.length} ${t("selectedUsers") || "selected users"}`}
                  </Typography>
                )}
                {waUEnabled && (
                  <Typography variant="caption" sx={{ color: "#25D366", fontWeight: 600 }}>
                    ✓ WhatsApp →{" "}
                    {waUMode === "all"
                      ? t("allUsers") || "all users"
                      : waUMode === "single"
                      ? `1 ${t("user") || "user"}`
                      : `${waUSelected.length} ${t("selectedUsers") || "selected users"}`}
                  </Typography>
                )}
                {waAEnabled && (
                  <Typography variant="caption" sx={{ color: "#9c27b0", fontWeight: 600 }}>
                    ✓ WhatsApp →{" "}
                    {waAMode === "all"
                      ? t("allAdmins") || "all admins"
                      : waAMode === "single"
                      ? `1 ${t("admin") || "admin"}`
                      : `${waASelected.length} ${t("selectedAdmins") || "selected admins"}`}
                  </Typography>
                )}
              </Box>
            </Alert>
          )}
        </DialogContent>

        <Divider />

        <DialogActions sx={{ p: 2.5, bgcolor: alpha(theme.palette.grey[500], 0.02) }}>
          <Button
            onClick={handleCloseSendDialog}
            disabled={sending}
            variant="outlined"
            size="large"
            sx={{ px: 3, borderRadius: 2, textTransform: "none", fontWeight: 600 }}
          >
            {t("cancel") || "Cancel"}
          </Button>
          <Button
            onClick={handleSendNotification}
            disabled={sending || !anyChannelSelected}
            variant="contained"
            color="success"
            size="large"
            startIcon={sending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
            sx={{
              px: 4,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 700,
              boxShadow: "0 4px 14px rgba(76, 175, 80, 0.4)",
              "&:hover": { boxShadow: "0 6px 20px rgba(76, 175, 80, 0.5)" },
            }}
          >
            {sending ? t("sending") || "Sending..." : t("sendNow") || "Send Now"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* SweetAlert custom styles */}
      <style>{`
        .swal-modern-popup {
          border-radius: 16px !important;
        }
        .swal2-html-container {
          padding: 20px !important;
        }
      `}</style>
    </Box>
  );
}